import React, { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import DesktopMenuItem from "./DesktopMenuItem";
import { StyledGrid } from "@hcl-commerce-store-sdk/react-component";
import {
  StyledButton,
  StyledBox,
  StyledListItemButton,
  StyledListItemIcon,
  StyledListItemText,
} from "../../../../styled-mui";
import { GetStaticLinksSelector } from "../../../../redux/selectors/category";
import { SvgIconProject } from "../../../svg/iconProject";
import AsyncCall from "../../../../_foundation/gtm/async.service";
import { CLICK } from "../../../../_foundation/constants/gtm";
import { getSite } from "../../../../_foundation/hooks/useSite";
import { LINK_USAGE, NAVIGATION_IDENTIFIERS } from "../../../../constants/marketing";
import DesktopProductsMenu from "./sub-menus/DesktopProductsMenu";
import DesktopInspirationMenu from "./sub-menus/DesktopInspirationMenu";
import DesktopServicesMenu from "./sub-menus/DesktopServicesMenu";
import DesktopOutilsMenu from "./sub-menus/DesktopOutilsMenu";
import { useNavigate } from "react-router";
import calque from "../../../svg/Calque.svg";

// COMPONENT MAP FOR LINKS/MENUS HARDCODED FROM FRONTEND BASED ON IDENTIFIER
const menuUiOnHover = {
  [NAVIGATION_IDENTIFIERS.PRODUCTS]: DesktopProductsMenu,
  [NAVIGATION_IDENTIFIERS.INSPIRATIONS]: DesktopInspirationMenu,
  [NAVIGATION_IDENTIFIERS.OUTILS]: DesktopOutilsMenu,
  [NAVIGATION_IDENTIFIERS.POSE_ET_SERVICE]: DesktopServicesMenu,
};

const DesktopMenuBar: React.FC<{
  menuLinksData: Record<string, any>;
  pageParams?: Record<string, any> | null;
  categories?: Record<string, any>[];
  pathname?: string;
}> = ({ menuLinksData, pageParams = null, categories = [], pathname = "" }) => {
  const mySite = getSite();
  const navigate = useNavigate();
  const staticLinksRedux: Record<string, any> = useSelector(GetStaticLinksSelector);
  const [selectedMenuItem, setSelectedMenuItem] = useState<string | null>(null);
  const popperRef: React.RefObject<HTMLButtonElement> = useRef<HTMLButtonElement>(null);
  const menuLinks = useMemo(() => {
    const headerLinks = (staticLinksRedux && staticLinksRedux?.headerLinks) || [];
    return [
      {
        usage: LINK_USAGE.STANDARD,
        seo: {
          label: "PRODUITS",
          target: "",
          type: "",
          url: "",
          identifier: NAVIGATION_IDENTIFIERS.PRODUCTS,
        },
      },
      ...headerLinks,
    ];
  }, [staticLinksRedux]);

  // Determine page type from pageParams
  const pageType = pageParams?.page?.type || "";

  // helper to check if item corresponds to magazine
  const isMagazineItem = (item: Record<string, any>) => {
    if (!item) return false;
    const label = (item?.seo?.label || "").toString().toLowerCase();
    const url = (item?.seo?.url || "").toString().toLowerCase();
    if (menuLinksData?.magazine && menuLinksData.magazine.label) {
      return label === menuLinksData.magazine.label.toString().toLowerCase();
    }
    return url.includes("magazine") || label.includes("magazine");
  };

  /**
   * Compute whether a top-level menu link should be obfuscated according to business rules.
   * - Product pages: obfuscate all main menu links
   * - Category pages (top-level/univers): keep top-level categories (sisters and current) as HTML
   * - Category pages (lower-level): keep sister categories and current; other links obfuscated
   * - Categories and product pages: magazine links must be obfuscated
   */
  const shouldObfuscate = (item: Record<string, any>) => {
    // safety
    if (!item || !item?.seo) return false;

    // If product page -> obfuscate everything in main menu
    if (typeof pageType === "string" && pageType.toLowerCase().includes("product")) return true;

    // If category page
    if (typeof pageType === "string" && pageType.toLowerCase().includes("category")) {
      // magazine links must be obfuscated
      if (isMagazineItem(item)) return true;

      // Determine whether current category is top-level (no parentCategoryId)
      const currentIdentifier = pageParams?.identifier || pageParams?.tokenValue || null;
      const currentParent = pageParams?.parentCategoryId || null;

      // Top-level category page: keep top-level categories visible (sisters + current)
      if (!currentParent) {
        // If the menu item represents a top-level category (present in categories list), keep it
        const itemIdentifier = item?.seo?.identifier || item?.seo?.label || "";
        const isTopLevel = categories?.some((c) => c?.identifier === itemIdentifier);
        return !isTopLevel; // obfuscate when NOT top-level
      }

      // Lower-level category page: keep sister categories (top-level) and current category
      const itemIdentifier2 = item?.seo?.identifier || item?.seo?.label || "";
      const isSisterOrCurrent =
        itemIdentifier2 === currentIdentifier || categories?.some((c) => c?.identifier === itemIdentifier2);
      return !isSisterOrCurrent;
    }

    // Default: do not obfuscate for other pages (home, content, etc.)
    return false;
  };

  return (
    <StyledBox className="expanded-menu-container">
      <StyledGrid container alignItems="center" overflow="hidden" justifyContent="space-between">
        <StyledGrid container item xs>
          <nav aria-label="Main Navigation">
            <StyledBox
              ref={popperRef}
              display="flex"
              direction="row"
              flexWrap="nowrap"
              justifyContent="flex-start"
              alignContent="flex-start"
              sx={{ flexBasis: { md: "inherit", lg: "auto" } }}
              ml={11}>
              {menuLinks.map((item: Record<string, any>, index: number) => (
                <DesktopMenuItem
                  key={item?.seo?.label || index}
                  index={index}
                  popperRef={popperRef}
                  isMenu={item?.usage !== LINK_USAGE.PROMO}
                  menuItem={item}
                  selectedMenuItem={selectedMenuItem}
                  setSelectedMenuItem={setSelectedMenuItem}
                  menuLinksData={menuLinksData}
                  OnHoverUI={menuUiOnHover?.[item?.seo?.identifier] || null}
                  obfuscate={shouldObfuscate(item)}
                  pageParams={pageParams}
                  categories={categories}
                  pathname={pathname}
                />
              ))}
            </StyledBox>
          </nav>
        </StyledGrid>
        <StyledGrid
          item
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}>
          {/* <StyledListItemButton
            component="a"
            href="tel:0801010538"
            className="right-margin-8"
            onClick={() => {
              AsyncCall.sendPageClickCtaEvent({
                event: "click_hotline",
                btn_label: "hotline",
              });
            }}
            sx={{
              cursor: "text",

              "&:hover": {
                backgroundColor: "transparent",
              },

              "&.MuiButtonBase-root:hover": {
                backgroundColor: "transparent",
              },
            }}>
            <StyledListItemIcon>
              <img
                src={calque}
                alt="hotline"
                style={{
                  width: 29,
                  height: 30,
                  objectFit: "contain",
                }}
              />
            </StyledListItemIcon>
            <StyledListItemText
              primary={"08 01 01 05 38"}
              primaryTypographyProps={{
                color: "#D73346",
                fontSize: "16px",
                fontWeight: "700",
                lineHeight: "24px",
                fontStyle: "ExtraBold",
                marginLeft: "8px",
              }}
            />
          </StyledListItemButton> */}
          <StyledButton
            // component="a"
            // href={mySite?.storeCfg?.userData?.PRO_SITE_URL}
            // target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              AsyncCall.sendPageClickCtaEvent({
                event: CLICK.ESCAPE_PRO,
                btn_label: "Escape Pro",
              });
              window.open(mySite?.storeCfg?.userData?.PRO_SITE_URL, "_blank", "noopener,noreferrer");
            }}
            startIcon={<SvgIconProject />}
            size="small"
            color="secondary"
            className="right-margin-8">
            Espace Pro
          </StyledButton>
        </StyledGrid>
      </StyledGrid>
    </StyledBox>
  );
};

export default DesktopMenuBar;
